<template>
	<v-card>
		<v-toolbar flat dense color="info" dark>
			<v-spacer></v-spacer>
			<v-toolbar-items>
				<v-btn small dark flat @click="$emit('onClose')" :disabled="isSubmitting || isValidating">Cancel</v-btn>
			</v-toolbar-items>
			<v-btn color="primary" small @click="submit" :disabled="isSubmitting || isValidating" :loading="isSubmitting || isValidating">Subscribe</v-btn>
		</v-toolbar>
		<v-card-text>
			<v-container grid-list-xl fluid>
				<v-layout row wrap>
					<v-flex md5>
						<ProductPlanDetailsTitleAndPrice v-if="productPlan" class="mt-2 mb-1" :pricing-option-id="pricingOptionId" :product-plan="productPlan" />
					</v-flex>
					<v-flex md7>
						<h2 class="mb-2">Payment Information</h2>
						<div v-if="tenant.hasStripeCustomer">
							<p>Your billing details are already saved. Subscribing will use your existing details.</p>
							<p>If you wish to update your billing details please contact support.</p>
						</div>
						<v-form ref="form" v-model="valid" v-else>
							<v-text-field v-model="form.customer.email" label="Billing Email" :error-messages="emailErrors" required box></v-text-field>

							<div id="card-details">
								<h3 class="text-xs-left mb-2">Card Details</h3>
								<stripe-element-card
									id="stripe-card"
									ref="stripeElementRef"
									:pk="publishableKey"
									@token="handleCardTokenReceived"
									@loading="handleCardLoading"
									@error="handleCardErrors"
									class="mb-4"
									hide-postal-code
									:element-style="{
										base: {
											fontSize: '16px',
											fontFamily: 'Avenir, Helvetica, Arial, sans-serif'
										}
									}"
								/>
								<v-text-field v-model="form.customer.name" label="Cardholder Name" :error-messages="nameErrors" required box></v-text-field>
							</div>

							<div>
								<h3 class="text-xs-left mb-2">Address Details</h3>
								<v-text-field v-model="form.customer.addressLine1" label="Address Line 1" :error-messages="addressLine1Errors" required box></v-text-field>
								<v-text-field v-model="form.customer.addressLine2" label="Address Line 2" box></v-text-field>
								<v-text-field v-model="form.customer.city" label="City" :error-messages="cityErrors" required box></v-text-field>
								<v-text-field v-model="form.customer.postalCode" label="Post Code" :error-messages="postCodeErrors" required box></v-text-field>
								<v-select v-model="form.customer.state" :items="states" label="State" :error-messages="stateErrors" required box></v-select>
							</div>
						</v-form>
					</v-flex>
				</v-layout>
			</v-container>
		</v-card-text>
		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn @click="$emit('onClose')" flat :disabled="isSubmitting || isValidating">Cancel</v-btn>
			<v-btn color="primary" @click="submit" :disabled="isSubmitting || isValidating" :loading="isSubmitting || isValidating">Subscribe</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
	import { StripeElementCard } from '@vue-stripe/vue-stripe';
	import { validationMixin } from 'vuelidate';
	import { required, email } from 'vuelidate/lib/validators';
	import { mapActions, mapGetters } from 'vuex';
	import config from '@common/config';
	import ProductPlanDetailsTitleAndPrice from '@components/settings/billing/ProductPlanDetailsTitleAndPrice.vue';

	export default {
		components: {
			ProductPlanDetailsTitleAndPrice,
			StripeElementCard
		},
		mixins: [validationMixin],
		props: {
			productPlan: {
				type: Object,
				default: null
			},
			pricingOptionId: {
				type: String,
				default: null
			}
		},
		data() {
			this.publishableKey = config.STRIPE_PUBLISH_KEY;
			return {
				isSubmitting: false,
				isValidating: false,
				valid: false,
				error: null,
				token: null,
				form: {
					customer: {
						name: '',
						email: '',
						addressLine1: '',
						addressLine2: '',
						city: '',
						country: 'AU',
						state: '',
						postalCode: ''
					}
				},
				states: ['NSW', 'VIC', 'QLD', 'NT', 'ACT', 'TAS', 'WA'],
				hasSubscribed: false
			};
		},
		validations: {
			form: {
				customer: {
					name: { required },
					email: { required, email },
					addressLine1: { required },
					city: { required },
					state: { required },
					postalCode: { required }
				}
			}
		},
		computed: {
			...mapGetters({
				tenant: 'loggedInTenant'
			}),
			nameErrors() {
				const errors = [];
				if (!this.$v.form.customer.name.$dirty) return errors;
				!this.$v.form.customer.name.required && errors.push('Cardholder Name is required.');
				return errors;
			},
			addressLine1Errors() {
				const errors = [];
				if (!this.$v.form.customer.addressLine1.$dirty) return errors;
				!this.$v.form.customer.addressLine1.required && errors.push('Address Line 1 is required.');
				return errors;
			},
			cityErrors() {
				const errors = [];
				if (!this.$v.form.customer.city.$dirty) return errors;
				!this.$v.form.customer.city.required && errors.push('City is required.');
				return errors;
			},
			stateErrors() {
				const errors = [];
				if (!this.$v.form.customer.state.$dirty) return errors;
				!this.$v.form.customer.state.required && errors.push('State is required.');
				return errors;
			},
			postCodeErrors() {
				const errors = [];
				if (!this.$v.form.customer.postalCode.$dirty) return errors;
				!this.$v.form.customer.postalCode.required && errors.push('Postal Code is required.');
				return errors;
			},
			emailErrors() {
				const errors = [];
				if (!this.$v.form.customer.email.$dirty) return errors;
				!this.$v.form.customer.email.email && errors.push('Must be valid e-mail');
				!this.$v.form.customer.email.required && errors.push('E-mail is required');
				return errors;
			},
			formValues() {
				return {
					customerDetails: {
						...this.form.customer,
						emailAddress: this.form.customer.email,
						cardToken: this.token ? this.token.id : null
					},
					productPlanId: this.productPlan.id,
					// TODO - Currently this is grabbing the first pricing option that is not hidden
					// When we offer multiple pricing options this will need to change
					pricingOptionId: this.pricingOptionId
				};
			}
		},
		methods: {
			...mapActions({
				subscribeToPlan: 'productPlans/subscribe'
			}),
			async submit() {
				if (this.tenant.hasStripeCustomer) {
					await this.subscribe();
					return;
				}
				// We need to wait for stripe to respond, so we set isSubmitting then handle the actual submission
				// after we hear back okay from stripe (handleCardTokenReceived).
				this.isValidating = true;
				this.$refs.stripeElementRef.submit();
				this.$v.form.$touch();
			},
			async handleCardTokenReceived(token) {
				this.token = token;
				if (!this.$v.form.$invalid) {
					await this.subscribe();
				}
			},
			async subscribe() {
				this.isSubmitting = true;
				const response = await this.subscribeToPlan(this.formValues);
				if (response.status === 200) {
					this.$emit('subscribed');
				}
				this.isSubmitting = false;
			},
			handleCardLoading(isLoading) {
				this.isValidating = isLoading;
			},
			handleCardErrors() {
				this.isValidating = false;
			}
		}
	};
</script>

<style lang="scss">
	#stripe-element-form {
		height: 56px;
	}

	#card-details {
		display: flex;
		flex-direction: column;
	}

	#stripe-element-mount-point {
		background: transparent;
		height: 100%;
		align-content: center;
		box-shadow: none;
		border: none;
	}

	#stripe-element-errors {
		text-align: left;
		padding: 5px 10px;
		font-size: 13px;
		color: #ff5252;
	}

	#stripe-card {
		background: #f0f0f0;
		border-bottom: 2px solid rgba(0, 0, 0, 0.42);
		border-radius: 4px 4px 0 0;
		transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
		&:hover {
			background: #e0e0e0;
			border-bottom: 2px solid rgba(0, 0, 0, 0.8);
		}
		&:has(#stripe-element-form .StripeElement--focus) {
			border-bottom: 2px solid #1976d2;
			&:hover {
				background: #f0f0f0;
			}
		}
		&:has(#stripe-element-form .StripeElement--invalid) {
			border-bottom: 2px solid #ff5252;
			&:hover {
				background: #f0f0f0;
			}
		}
	}
</style>
